import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/LanguageContext';
import { apiService } from '../services/api';
import { AppointmentResponse, UserResponse, TimeslotInfo, TimeslotClientsBatchItem, TimeslotClientsBatchResponse, CancellationDeadlineSettingsResponse } from '../types/api';
import { useConfirmation } from '../hooks/useConfirmation';
import AddClientModal from './AddClientModal';
import EditClientInfoModal from './EditClientInfoModal';
import EditClientSubscriptionModal from './EditClientSubscriptionModal';
import EditClientAppointmentsModal from './EditClientAppointmentsModal';
import BodyFatMeasurementsModal from './BodyFatMeasurementsModal';
import TimeslotManagement from './TimeslotManagement';
import Settings from './Settings';
import BookAppointment from './BookAppointment';
import ClientProfile from './ClientProfile';
import ConfirmationDialog from './ConfirmationDialog';
import AdminCancelAppointmentDialog from './AdminCancelAppointmentDialog';
import LanguageSelector from './LanguageSelector';
import { toGreekUpperCase } from '../utils/greekUtils';
import './Dashboard.css';

type AdminTab = 'overview' | 'subscriptions' | 'clients' | 'timeslots' | 'settings';
type ClientTab = 'overview' | 'book' | 'profile';

interface ClientSubscriptionData {
  client: UserResponse;
  appointments: AppointmentResponse[];
  monthlyAppointments: AppointmentResponse[];
  appointmentDates: string[];
}

interface UpcomingTimeslotData {
  timeslot: TimeslotInfo;
  clientsData: TimeslotClientsBatchItem | null;
}

const Dashboard: React.FC = () => {
  const { user, logout, isAdmin, isClient } = useAuth();
  const { t, formatDate: formatDateWithLocale } = useTranslation();
  const [appointments, setAppointments] = useState<AppointmentResponse[]>([]);
  const [clients, setClients] = useState<UserResponse[]>([]);
  const [allClients, setAllClients] = useState<UserResponse[]>([]);
  const [filteredClients, setFilteredClients] = useState<UserResponse[]>([]);
  const [clientProfile, setClientProfile] = useState<UserResponse | null>(null);
  const [, setClientSubscriptionData] = useState<ClientSubscriptionData[]>([]);
  const [unfilteredSubscriptionData, setUnfilteredSubscriptionData] = useState<ClientSubscriptionData[]>([]);
  const [subscriptionSearchTerm, setSubscriptionSearchTerm] = useState<string>('');
  const [subscriptionDataLoading, setSubscriptionDataLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [clientFilter, setClientFilter] = useState<'all' | 'active' | 'inactive' | null>(null);
  const [showWelcome, setShowWelcome] = useState(true);
  const { confirmation, showConfirmation, hideConfirmation } = useConfirmation();
  const [cancelDialogAppointmentId, setCancelDialogAppointmentId] = useState<number | null>(null);
  const [isCancellingAsAdmin, setIsCancellingAsAdmin] = useState(false);
  
  // Wrapper function for setError with auto-dismiss
  const setErrorWithAutoDismiss = (message: string) => {
    setError(message);
    // Clear error message after 3 seconds
    setTimeout(() => {
      setError('');
    }, 3000);
  };
  
  // Admin state
  const [activeAdminTab, setActiveAdminTab] = useState<AdminTab>('overview');
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [showEditInfoModal, setShowEditInfoModal] = useState(false);
  const [showEditSubscriptionModal, setShowEditSubscriptionModal] = useState(false);
  const [showEditAppointmentsModal, setShowEditAppointmentsModal] = useState(false);
  const [showBodyFatModal, setShowBodyFatModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState<UserResponse | null>(null);
  const [settings, setSettings] = useState<any[]>([]);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsError, setSettingsError] = useState<string>('');
  
  // Upcoming appointments state
  const [upcomingAppointmentsDate, setUpcomingAppointmentsDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [upcomingTimeslots, setUpcomingTimeslots] = useState<UpcomingTimeslotData[]>([]);
  const [upcomingAppointmentsLoading, setUpcomingAppointmentsLoading] = useState(false);
  const [removingAppointments, setRemovingAppointments] = useState<Set<number>>(new Set());
  
  // Client state
  const [activeClientTab, setActiveClientTab] = useState<ClientTab>('overview');
  
  // Refs to prevent duplicate fetches
  const clientsFetchedRef = useRef(false);
  const fetchingRef = useRef(false);
  const settingsFetchedRef = useRef(false);
  const initialLoadRef = useRef(true);
  const subscriptionInitialLoadRef = useRef(true);


  const filteredSubscriptionData = useMemo(() => {
    return subscriptionSearchTerm.trim()
      ? unfilteredSubscriptionData.filter(data =>
          data.client.name.toLowerCase().includes(subscriptionSearchTerm.toLowerCase()) ||
          data.client.email.toLowerCase().includes(subscriptionSearchTerm.toLowerCase())
        )
      : unfilteredSubscriptionData;
  }, [subscriptionSearchTerm, unfilteredSubscriptionData]);

  const activeClientsCount = useMemo(() => allClients.filter(c =>
    c.isActive &&
    c.subscription &&
    c.subscription.isActive &&
    new Date(c.subscription.actualEndDate || c.subscription.endDate) > new Date()
  ).length, [allClients]);

  const inactiveClientsCount = useMemo(() => allClients.filter(c =>
    !c.isActive ||
    !c.subscription ||
    !c.subscription.isActive ||
    new Date(c.subscription.actualEndDate || c.subscription.endDate) <= new Date()
  ).length, [allClients]);



  const updateFilteredClients = (searchTermToUse: string) => {
    let filtered = allClients;

    // Apply search filter
    if (searchTermToUse.trim()) {
      filtered = filtered.filter(client =>
        client.name.toLowerCase().includes(searchTermToUse.toLowerCase()) ||
        client.email.toLowerCase().includes(searchTermToUse.toLowerCase())
      );
    }

    // Apply status filter (only if a filter is active)
    if (clientFilter === 'active') {
      filtered = filtered.filter(c =>
        c.isActive &&
        c.subscription &&
        c.subscription.isActive &&
        new Date(c.subscription.actualEndDate || c.subscription.endDate) > new Date()
      );
    } else if (clientFilter === 'inactive') {
      filtered = filtered.filter(c =>
        !c.isActive ||
        !c.subscription ||
        !c.subscription.isActive ||
        new Date(c.subscription.actualEndDate || c.subscription.endDate) <= new Date()
      );
    }
    // If clientFilter is 'all' or null, show all clients (no status filter)

    setFilteredClients(filtered);
  };

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, isClient]);

  useEffect(() => {
    // Start the flip animation 2 seconds after page loads
    const timer = setTimeout(() => {
      setShowWelcome(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  // Re-filter clients when clientFilter changes
  useEffect(() => {
    if (allClients.length > 0) {
      updateFilteredClients(searchTerm);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientFilter]);

  useEffect(() => {
    // Listen for appointment booking/cancelling events to refresh data
    const handleAppointmentBooked = () => {
      if (isClient()) {
        fetchData(); // Refresh appointments for client
      } else if (isAdmin()) {
        // Force refresh clients and subscription data
        clientsFetchedRef.current = false;
        fetchClients(); // This will refresh client list and subscription data if on overview tab
        // Also refresh upcoming appointments and force refresh subscription data
        if (isAdmin()) {
          fetchUpcomingAppointments(upcomingAppointmentsDate);
          refreshSubscriptionData(); // Always refresh subscription data for overview
        }
      }
    };

    const handleAppointmentCancelled = () => {
      if (isClient()) {
        fetchData(); // Refresh appointments for client
      } else if (isAdmin()) {
        // Force refresh clients and subscription data
        clientsFetchedRef.current = false;
        fetchClients(); // This will refresh client list and subscription data if on overview tab
        // Also refresh upcoming appointments and force refresh subscription data
        if (isAdmin()) {
          fetchUpcomingAppointments(upcomingAppointmentsDate);
          refreshSubscriptionData(); // Always refresh subscription data for overview
        }
      }
    };

    window.addEventListener('appointmentBooked', handleAppointmentBooked);
    window.addEventListener('appointmentCancelled', handleAppointmentCancelled);
    
    return () => {
      window.removeEventListener('appointmentBooked', handleAppointmentBooked);
      window.removeEventListener('appointmentCancelled', handleAppointmentCancelled);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClient, isAdmin, upcomingAppointmentsDate]);

  useEffect(() => {
    // Fetch upcoming appointments when date changes or when overview tab is active
    if (isAdmin() && activeAdminTab === 'overview') {
      fetchUpcomingAppointments(upcomingAppointmentsDate);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upcomingAppointmentsDate, activeAdminTab, isAdmin]);

  const fetchData = async (options?: { showLoader?: boolean }) => {
    // Prevent overlapping fetch calls
    if (fetchingRef.current) {
      return;
    }
    
    try {
      fetchingRef.current = true;
      const showLoader = options?.showLoader ?? initialLoadRef.current;
      if (showLoader) {
        setIsLoading(true);
      }
      setError('');

      if (isClient()) {
        // Fetch client appointments and profile
        const [appointmentsResponse, profileResponse] = await Promise.all([
          apiService.getAppointments(),
          apiService.getProfile()
        ]);
        
        if (appointmentsResponse.success && appointmentsResponse.data) {
          setAppointments(appointmentsResponse.data);
        }
        
        if (profileResponse.success && profileResponse.data) {
          setClientProfile(profileResponse.data);
        }
      } else if (isAdmin()) {
        // Fetch clients for admin (only if not already fetched)
        if (!clientsFetchedRef.current) {
          await fetchClients();
        }
      }
    } catch (err: any) {
      console.error('Error fetching data:', err);
      setError(t('failedToLoadData'));
    } finally {
      if (options?.showLoader ?? initialLoadRef.current) {
        setIsLoading(false);
      }
      initialLoadRef.current = false;
      fetchingRef.current = false;
    }
  };

  const fetchClients = async (search?: string) => {
    try {
      const searchToUse = search !== undefined ? search : searchTerm;
      // Always fetch ALL clients first to maintain accurate stats
      const response = await apiService.getClients('');

      if (response.success && response.data) {
        // Update all clients state (used for stats counters)
        setAllClients(response.data);
        setClients(response.data);

        // Apply local filtering for display
        let filtered = response.data;

        // Apply search filter
        if (searchToUse.trim()) {
          filtered = filtered.filter(client =>
            client.name.toLowerCase().includes(searchToUse.toLowerCase()) ||
            client.email.toLowerCase().includes(searchToUse.toLowerCase())
          );
        }

        // Apply status filter (only if a filter is active)
        if (clientFilter === 'active') {
          filtered = filtered.filter(c =>
            c.isActive &&
            c.subscription &&
            c.subscription.isActive &&
            new Date(c.subscription.actualEndDate || c.subscription.endDate) > new Date()
          );
        } else if (clientFilter === 'inactive') {
          filtered = filtered.filter(c =>
            !c.isActive ||
            !c.subscription ||
            !c.subscription.isActive ||
            new Date(c.subscription.actualEndDate || c.subscription.endDate) <= new Date()
          );
        }
        // If clientFilter is 'all' or null, show all clients (no status filter)

        setFilteredClients(filtered);

        // Update subscription data for overview and subscriptions tabs
        if (activeAdminTab === 'overview' || activeAdminTab === 'subscriptions') {
          await fetchClientSubscriptionData(response.data);
        }
      }

      clientsFetchedRef.current = true;
    } catch (err: any) {
      console.error('Error fetching clients:', err);
      setError(t('failedToLoadClients'));
      clientsFetchedRef.current = true;
    }
  };

  const fetchSettings = async () => {
    if (settingsFetchedRef.current && settings.length > 0) return;
    
    try {
      setSettingsLoading(true);
      setSettingsError('');
      const response = await apiService.getSettings();
      if (response.success && response.data) {
        setSettings(response.data);
      } else {
        setSettingsError(t('failedToLoadSettings'));
      }
      settingsFetchedRef.current = true;
    } catch (err: any) {
      console.error('Error fetching settings:', err);
      setSettingsError('Failed to load settings');
      settingsFetchedRef.current = true; // Set flag even on error to prevent infinite retries
    } finally {
      setSettingsLoading(false);
    }
  };

  const refreshSettings = async () => {
    // Force refresh by resetting the flag
    settingsFetchedRef.current = false;
    await fetchSettings();
  };

  const refreshSubscriptionData = async () => {
    if (isAdmin() && clients.length > 0) {
      await fetchClientSubscriptionData(clients);
    }
  };

  const fetchUpcomingAppointments = async (date: string) => {
    try {
      setUpcomingAppointmentsLoading(true);
      
      // Step 1: Get basic timeslot information
      const timeslotsResponse = await apiService.getTimeslots(date);
      
      if (timeslotsResponse.success && timeslotsResponse.data) {
        const now = new Date();
        const currentTime = now.getHours() * 60 + now.getMinutes();
        const isToday = date === new Date().toISOString().split('T')[0];
        
        // Filter timeslots to show upcoming ones with some lazy tolerance
        const relevantTimeslots = timeslotsResponse.data.timeslots.filter(timeslot => {
          if (!isToday) {
            // For future dates, show all timeslots
            return true;
          }
          
          // For today, show timeslots that are within 90 minutes of current time (lazy about time limits)
          const [hours, minutes] = timeslot.startTime.split(':').map(Number);
          const slotTime = hours * 60 + minutes;
          const timeDifference = slotTime - currentTime;
          
          // Show timeslots that start within the next 24 hours or started within the last 90 minutes
          return (timeDifference >= -90);
        });
        
        // Step 2: Fetch booked clients for every timeslot on this date in one call
        let batchTimeslots: TimeslotClientsBatchResponse | undefined;
        try {
          const batchResponse = await apiService.getTimeslotClientsBatch(date);
          batchTimeslots = batchResponse.success ? batchResponse.data : undefined;
        } catch (err) {
          console.error(`Error fetching timeslot clients batch for ${date}:`, err);
        }

        const upcomingData: UpcomingTimeslotData[] = relevantTimeslots.map((timeslot) => {
          // null means "failed to load" (renders an error state); an item with an empty
          // bookedClients array means "loaded successfully, nothing booked" - these are NOT
          // the same thing. A timeslot with zero bookings has no group in the batch response,
          // so a missing match must default to the empty-but-successful shape, not null.
          if (!batchTimeslots) {
            return { timeslot, clientsData: null };
          }

          const found = batchTimeslots.timeslots.find(
            t => t.startTime === timeslot.startTime && t.endTime === timeslot.endTime
          );
          const clientsData = found ?? {
            startTime: timeslot.startTime,
            endTime: timeslot.endTime,
            bookedCount: 0,
            bookedClients: []
          };

          return { timeslot, clientsData };
        });

        setUpcomingTimeslots(upcomingData);
      }
    } catch (err: any) {
      console.error('Error fetching upcoming appointments:', err);
    } finally {
      setUpcomingAppointmentsLoading(false);
    }
  };

  const fetchClientSubscriptionData = async (clientList: UserResponse[]) => {
    try {
      const shouldShowLoader = subscriptionInitialLoadRef.current;
      if (shouldShowLoader) {
        setSubscriptionDataLoading(true);
      }
      
      const activeSubscriptionClients = clientList.filter(client =>
        client.isActive &&
        client.subscription &&
        client.subscription.isActive &&
        !client.subscription.isFrozen &&
        new Date(client.subscription.actualEndDate || client.subscription.endDate) > new Date()
      );

      // Fetch cancellation deadline settings once for all clients
      let deadlineSettings: CancellationDeadlineSettingsResponse | null = null;
      try {
        const response = await apiService.getCancellationDeadlineSettings();
        if (response.success && response.data) {
          deadlineSettings = response.data;
        }
      } catch (error) {
        console.error('Failed to get cancellation deadline settings:', error);
      }

      const subscriptionData: ClientSubscriptionData[] = [];

      for (const client of activeSubscriptionClients) {
        try {
          if (!client.subscription) continue;
          
          // Use appointments from the client data if available
          const allAppointments = client.appointments || [];
          
          // Get current month start and end dates to filter monthly appointments
          const now = new Date();
          const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
          const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
          
          // Filter appointments for current month that count towards monthly limit (active and completed, not cancelled)
          const monthlyAppointments = allAppointments.filter(apt => {
            const aptDate = new Date(apt.appointmentDate);
            return (apt.status === 1 || apt.status === 3) && 
                   aptDate >= monthStart && 
                   aptDate <= monthEnd;
          });
          
          // Get appointment dates within subscription period for display
          // Include: completed, future, and canceled appointments that count as valid (past deadline)
          const subscriptionStart = new Date(client.subscription.actualStartDate || client.subscription.startDate);
          const subscriptionEnd = new Date(client.subscription.actualEndDate || client.subscription.endDate);
          
          const validAppointments = allAppointments.filter(apt => {
            const aptDate = new Date(apt.appointmentDate);
            // Must be within subscription period
            if (!(aptDate >= subscriptionStart && aptDate <= subscriptionEnd)) {
              return false;
            }
            
            // Include completed appointments
            if (apt.status === 3) return true;
            
            // Include future appointments  
            if (apt.status === 1) return true;
            
            // For canceled appointments, exclude admin cancellations and check deadline for client cancellations
            if (apt.status === 2 && apt.cancelledAt) {
              // Exclude appointments cancelled by admin (cancelledBy=1)
              if (apt.cancelledBy === 1) {
                return false;
              }
              
              // For client cancellations, check if they were canceled after deadline (making them valid)
              if (deadlineSettings) {
                const cancelledAtTime = new Date(apt.cancelledAt);
                
                // Use the existing checkCancellationDeadline function with the cancellation time
                // If it returns false, the cancellation was past deadline and counts as valid
                const wasWithinDeadline = checkCancellationDeadline(apt, deadlineSettings, client, cancelledAtTime);
                return !wasWithinDeadline; // Include if it was past deadline (not within deadline)
              }
            }
            
            return false;
          });
          
          const appointmentDates = validAppointments.map(apt => apt.appointmentDate);

          subscriptionData.push({
            client,
            appointments: allAppointments,
            monthlyAppointments,
            appointmentDates
          });
        } catch (err) {
          console.error(`Error processing client ${client.id}:`, err);
          // Include client even if processing fails
          subscriptionData.push({
            client,
            appointments: [],
            monthlyAppointments: [],
            appointmentDates: []
          });
        }
      }

      setClientSubscriptionData(subscriptionData);
      setUnfilteredSubscriptionData(subscriptionData);
    } catch (err) {
      console.error('Error fetching client subscription data:', err);
    } finally {
      setSubscriptionDataLoading(false);
      subscriptionInitialLoadRef.current = false;
    }
  };

  const isSubscriptionExpired = (profile: UserResponse | null): boolean => {
    if (!profile || !profile.subscription) {
      return true; // No subscription means expired
    }
    
    const now = new Date();
    // Use actualEndDate if available, otherwise fall back to endDate
    const endDateStr = profile.subscription.actualEndDate || profile.subscription.endDate;
    const endDate = new Date(endDateStr);
    
    return endDate < now;
  };

  const getSubscriptionStatus = (endDate: string, isFrozen?: boolean, userIsActive?: boolean) => {
    if (userIsActive === false) {
      return 'disabled';
    }
    
    if (isFrozen) {
      return 'frozen';
    }
    
    const end = new Date(endDate);
    const now = new Date();
    const sevenDaysFromNow = new Date();
    sevenDaysFromNow.setDate(now.getDate() + 7);

    if (end < now) {
      return 'expired';
    } else if (end <= sevenDaysFromNow) {
      return 'expiring';
    } else {
      return 'active';
    }
  };

  const formatDateRange = (startDate: string, endDate: string) => {
    const startFormatted = formatDateWithLocale(startDate, { short: true });
    const endFormatted = formatDateWithLocale(endDate, { short: true });
    
    return `${startFormatted} - ${endFormatted}`;
  };

  const formatAppointmentDates = (dates: string[]) => {
    if (dates.length === 0) return t('noAppointmentHistory');

    const sortedDates = dates.sort();

    const formatted = sortedDates.map(date => {
    const d = new Date(date);
    return new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'short',
      // 👇 no year here
    }).format(d);
    });

    return formatted.join(', ');
  };


  const handleSubscriptionSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = e.target.value;
    setSubscriptionSearchTerm(searchTerm);
  };

  const handleLogout = () => {
    logout();
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value;
    setSearchTerm(searchValue);
    updateFilteredClients(searchValue);
  };

  const handleClientFilterChange = (filter: 'all' | 'active' | 'inactive') => {
    // If clicking the same filter, reset to no filter (null)
    if (clientFilter === filter) {
      setClientFilter(null);
    } else {
      setClientFilter(filter);
    }
  };

  const handleAddClient = () => {
    setShowAddClientModal(true);
  };

  const handleEditInfo = (client: UserResponse) => {
    setSelectedClient(client);
    setShowEditInfoModal(true);
  };

  const handleEditSubscription = (client: UserResponse) => {
    setSelectedClient(client);
    setShowEditSubscriptionModal(true);
  };

  const handleEditAppointments = (client: UserResponse) => {
    setSelectedClient(client);
    setShowEditAppointmentsModal(true);
  };

  const handleBodyFatMeasurements = (client: UserResponse) => {
    setSelectedClient(client);
    setShowBodyFatModal(true);
  };

  const handleClientAdded = () => {
    clientsFetchedRef.current = false;
    fetchClients();
  };

  const handleClientUpdated = async () => {
    clientsFetchedRef.current = false;
    await fetchClients();
  };

  const handleCancelAppointment = async (appointmentId: number) => {
    // Admins canceling on behalf of a client always go through the admin cancel dialog,
    // which lets them choose whether it counts toward the client's monthly limit -
    // the client-side deadline warning below doesn't apply to admin-initiated cancellations.
    if (isAdmin()) {
      setCancelDialogAppointmentId(appointmentId);
      return;
    }

    // Find the appointment details
    const appointment = appointments.find(apt => apt.id === appointmentId) ||
                       upcomingTimeslots.flatMap(ts => ts.clientsData?.bookedClients || []).find(apt => apt.id === appointmentId);

    if (!appointment) {
      setError('Appointment not found');
      return;
    }

    try {
      // Get cancellation deadline settings
      const deadlineResponse = await apiService.getCancellationDeadlineSettings();
      if (!deadlineResponse.success || !deadlineResponse.data) {
        console.error('Failed to get cancellation deadline settings');
        // Proceed with normal cancellation if we can't get settings
        showNormalCancellationDialog(appointmentId);
        return;
      }

      const deadlineSettings = deadlineResponse.data;
      const currentProfile = clientProfile;

      if (!currentProfile) {
        console.error('No user profile available');
        showNormalCancellationDialog(appointmentId);
        return;
      }

      const isWithinDeadline = checkCancellationDeadline(appointment, deadlineSettings, currentProfile);

      if (isWithinDeadline) {
        // Normal cancellation - within deadline
        showNormalCancellationDialog(appointmentId);
      } else {
        // Past deadline - show warning
        showDeadlineWarningDialog(appointmentId);
      }
    } catch (error) {
      console.error('Error checking cancellation deadline:', error);
      // Fallback to normal cancellation if there's an error
      showNormalCancellationDialog(appointmentId);
    }
  };

  const handleConfirmCancelAsAdmin = async (countTowardLimit: boolean) => {
    if (cancelDialogAppointmentId === null) return;
    const appointmentId = cancelDialogAppointmentId;

    setIsCancellingAsAdmin(true);
    try {
      await confirmCancelAppointment(appointmentId, countTowardLimit);
    } finally {
      setIsCancellingAsAdmin(false);
      setCancelDialogAppointmentId(null);
    }
  };

  const showNormalCancellationDialog = (appointmentId: number) => {
    showConfirmation({
      title: t('cancelAppointment'),
      message: t('confirmCancelAppointmentMessage'),
      confirmText: t('cancelAppointment'),
      type: 'danger',
      onConfirm: () => {
        confirmCancelAppointment(appointmentId);
      }
    });
  };

  const showDeadlineWarningDialog = (appointmentId: number) => {
    showConfirmation({
      title: t('cancelAppointment'),
      message: t('cancellationDeadlineWarning'),
      confirmText: t('yes'),
      cancelText: t('no'),
      type: 'warning',
      onConfirm: () => {
        confirmCancelAppointment(appointmentId);
      }
    });
  };

  const checkCancellationDeadline = (appointment: AppointmentResponse, deadlineSettings: CancellationDeadlineSettingsResponse, profile: UserResponse, currentTime?: Date): boolean => {
    const now = currentTime || new Date();
    const appointmentDate = new Date(appointment.appointmentDate);
    const appointmentStartTime = appointment.startTime;
    
    // Return true if startTime is missing (treat as within deadline to be safe)
    if (!appointmentStartTime) {
      return true;
    }

    // Check if this is a morning appointment by comparing start time to cutoff time
    // API returns times in local Greece time, no timezone conversion needed
    const [appointmentHours, appointmentMinutes] = appointmentStartTime.split(':').map(Number);
    const [cutoffHours, cutoffMinutes] = deadlineSettings.morningAppointmentCutoffTime.split(':').map(Number);

    const appointmentTimeInMinutes = appointmentHours * 60 + appointmentMinutes;
    const cutoffTimeInMinutes = cutoffHours * 60 + cutoffMinutes;
    const isMorningAppointment = appointmentTimeInMinutes < cutoffTimeInMinutes;

    if (isMorningAppointment) {
      // Morning appointment logic - deadline is at a specific time the day before
      // API returns times in local Greece time, no timezone conversion needed
      const deadlineTime = deadlineSettings.morningClientCancellationDeadlineTime; // e.g., "15:00"
      const [deadlineHours, deadlineMinutes] = deadlineTime.split(':').map(Number);

      // Calculate deadline datetime (previous day at deadline time)
      const deadlineDateTime = new Date(appointmentDate);
      deadlineDateTime.setDate(deadlineDateTime.getDate() - 1);
      deadlineDateTime.setHours(deadlineHours, deadlineMinutes, 0, 0);

      return now <= deadlineDateTime;
    } else {
      // Regular appointment logic - deadline is X hours before appointment
      const deadlineHours = parseInt(deadlineSettings.regularClientCancellationDeadlineTime); // e.g., "12"
      
      // Calculate appointment datetime
      const appointmentDateTime = new Date(appointmentDate);
      appointmentDateTime.setHours(appointmentHours, appointmentMinutes, 0, 0);
      
      // Calculate deadline datetime (X hours before appointment)
      const deadlineDateTime = new Date(appointmentDateTime);
      deadlineDateTime.setHours(deadlineDateTime.getHours() - deadlineHours);
      
      return now <= deadlineDateTime;
    }
  };

  const confirmCancelAppointment = async (appointmentId: number, countTowardLimit?: boolean) => {
    // Find the appointment details for success message
    const appointment = appointments.find(apt => apt.id === appointmentId);

    try {
      // Add appointment ID to removing set to trigger animation
      setRemovingAppointments(prev => new Set(prev).add(appointmentId));

      const response = isAdmin()
        ? await apiService.cancelAppointmentAsAdmin(appointmentId, countTowardLimit ?? false)
        : await apiService.cancelAppointment(appointmentId);
      if (response.success) {
        hideConfirmation(); // Close the confirmation dialog
        
        // Show success message with appointment details
        if (appointment && isClient()) {
          const formattedDate = formatDate(appointment.appointmentDate);
          const formattedTime = `${formatTime(appointment.startTime)} - ${formatTime(appointment.endTime)}`;
          const successMessage = t('appointmentCancelledFor')
            .replace('{date}', formattedDate)
            .replace('{time}', formattedTime);
          setSuccess(successMessage);
          
          // Clear success message after 5 seconds
          setTimeout(() => {
            setSuccess('');
          }, 5000);
        }
        
        // Wait for animation to complete before removing from state
        setTimeout(() => {
          // Optimistically update the UI by removing the appointment from the current state
          setUpcomingTimeslots(prevTimeslots => 
            prevTimeslots.map(timeslotData => ({
              ...timeslotData,
              clientsData: timeslotData.clientsData && {
                ...timeslotData.clientsData,
                bookedClients: timeslotData.clientsData.bookedClients?.filter(
                  appointment => appointment.id !== appointmentId
                ) || [],
                bookedCount: (timeslotData.clientsData.bookedCount || 0) - 1
              }
            }))
          );
          
          // Remove from removing set
          setRemovingAppointments(prev => {
            const newSet = new Set(prev);
            newSet.delete(appointmentId);
            return newSet;
          });
        }, 300); // Match the CSS transition duration
        
        // Refresh data to ensure consistency
        setTimeout(() => {
          if (isAdmin()) {
            fetchUpcomingAppointments(upcomingAppointmentsDate);
          } else {
            // For client, refresh their appointments
            fetchData();
          }
        }, 350);
      } else {
        // Remove from removing set on error
        setRemovingAppointments(prev => {
          const newSet = new Set(prev);
          newSet.delete(appointmentId);
          return newSet;
        });
        
        hideConfirmation(); // Close the confirmation dialog even on error
        setError(response.message || 'Failed to cancel appointment');
        
        // Clear error message after 6 seconds
        setTimeout(() => {
          setError('');
        }, 6000);
      }
    } catch (err: any) {
      // Remove from removing set on error
      setRemovingAppointments(prev => {
        const newSet = new Set(prev);
        newSet.delete(appointmentId);
        return newSet;
      });
      
      hideConfirmation(); // Close the confirmation dialog even on error
      setError(err.response?.data?.message || 'Error cancelling appointment');
      
      // Clear error message after 6 seconds
      setTimeout(() => {
        setError('');
      }, 6000);
    }
  };

  const formatDate = (dateString: string, options?: { weekday?: boolean; short?: boolean }) => {
    return formatDateWithLocale(dateString, options || { short: true });
  };

  const formatTime = (timeString: string) => {
    const parts = timeString.split(':');
    if (parts.length >= 2) {
      const hours = parseInt(parts[0]);
      const minutes = parts[1];
      return `${hours.toString().padStart(2, '0')}:${minutes}`;
    }
    return timeString;
  };

  const getStatusBadge = (status: number) => {
    switch (status) {
      case 1:
        return <span className="status-badge booked">{toGreekUpperCase(t('confirmed'))}</span>;
      case 2:
        return <span className="status-badge cancelled">{toGreekUpperCase(t('cancelled'))}</span>;
      case 3:
        return <span className="status-badge completed">{toGreekUpperCase(t('completed'))}</span>;
      default:
        return <span className="status-badge">Unknown</span>;
    }
  };

  const getUpcomingAppointments = () => {
    const now = new Date();

    // Show booked appointments (status 1) that are in the future
    return appointments.filter(apt => {
      if (apt.status !== 1) return false; // Only show active/booked appointments

      // Create date object from appointment date and end time
      const appointmentDateTime = new Date(apt.appointmentDate);
      const [hours, minutes] = apt.endTime.split(':').map(Number);
      appointmentDateTime.setHours(hours, minutes, 0, 0);

      // Only show appointments that haven't ended yet
      return appointmentDateTime > now;
    });
  };

  const getPastAppointments = () => {
    // Show completed appointments (status 3) and canceled appointments (status 2) in history
    return appointments
      .filter(apt => apt.status === 2 || apt.status === 3) // Canceled or completed appointments
      .sort((a, b) => {
        // Sort by latest update time (greatest of bookedAt and cancelledAt) descending
        const getLatestUpdateTime = (appointment: AppointmentResponse) => {
          const bookedTime = new Date(appointment.bookedAt).getTime();
          const cancelledTime = appointment.cancelledAt ? new Date(appointment.cancelledAt).getTime() : 0;
          return Math.max(bookedTime, cancelledTime);
        };
        
        return getLatestUpdateTime(b) - getLatestUpdateTime(a);
      });
  };

  const handleUpcomingAppointmentsDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUpcomingAppointmentsDate(e.target.value);
  };

  const navigateUpcomingAppointmentsDate = (direction: 'prev' | 'next') => {
    const currentDate = new Date(upcomingAppointmentsDate);
    if (direction === 'prev') {
      currentDate.setDate(currentDate.getDate() - 1);
    } else {
      currentDate.setDate(currentDate.getDate() + 1);
    }
    setUpcomingAppointmentsDate(currentDate.toISOString().split('T')[0]);
  };

  if (isLoading) {
    return (
      <div className="dashboard-container">
        <div className="loading">{t('loading')}</div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="header-content">
          <div className="user-info">
            <div className="welcome-container">
              <h1 className={`welcome-text ${showWelcome ? 'show' : 'hide'}`}>
                {t('welcome')}, {user?.name}!
              </h1>
              <h1 className={`reforged-text ${showWelcome ? 'hide' : 'show'}`}>
                Reforged
              </h1>
            </div>
            <p className="user-role">{isAdmin() ? t('administrator') : t('client')}</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <LanguageSelector />
            <button onClick={handleLogout} className="logout-button">
              {t('logout')}
            </button>
          </div>
        </div>
      </header>

      {error && (
        <div className="error-banner">
          {error}
        </div>
      )}

      {success && (
        <div className="success-banner">
          {success}
        </div>
      )}

      <main className="dashboard-main">
        {isAdmin() && (
          <div className="admin-dashboard">
            {/* Admin Navigation */}
            <nav className="dashboard-nav">
              <button
                className={`nav-tab ${activeAdminTab === 'overview' ? 'active' : ''}`}
                onClick={async () => {
                  setActiveAdminTab('overview');
                  setSubscriptionSearchTerm('');
                  clientsFetchedRef.current = false;
                  await fetchClients('');
                }}
              >
                {t('overview')}
              </button>
              <button
                className={`nav-tab ${activeAdminTab === 'subscriptions' ? 'active' : ''}`}
                onClick={async () => {
                  setActiveAdminTab('subscriptions');
                  setSubscriptionSearchTerm('');
                  clientsFetchedRef.current = false;
                  await fetchClients('');
                }}
              >
                {t('activeSubscriptions')}
              </button>
              <button
                className={`nav-tab ${activeAdminTab === 'clients' ? 'active' : ''}`}
                onClick={async () => {
                  setActiveAdminTab('clients');
                  setSearchTerm('');
                  setSubscriptionDataLoading(false);
                  clientsFetchedRef.current = false;
                  await fetchClients('');
                }}
              >
                {t('clientManagement')}
              </button>
              <button
                className={`nav-tab ${activeAdminTab === 'timeslots' ? 'active' : ''}`}
                onClick={() => setActiveAdminTab('timeslots')}
              >
                {t('timeslotManagement')}
              </button>
              <button 
                className={`nav-tab ${activeAdminTab === 'settings' ? 'active' : ''}`}
                onClick={() => {
                  setActiveAdminTab('settings');
                  fetchSettings();
                }}
              >
                {t('settings')}
              </button>
            </nav>

            {/* Admin Content */}
            {activeAdminTab === 'overview' && (
              <div className="admin-overview">
                <section className="upcoming-appointments-admin">
                  <div className="section-header">
                    <h2>{t('upcomingAppointments')}</h2>
                    <div className="date-navigation">
                      <button 
                        className="nav-arrow"
                        onClick={() => navigateUpcomingAppointmentsDate('prev')}
                        title="Previous day"
                      >
                        ←
                      </button>
                      <input
                        type="date"
                        value={upcomingAppointmentsDate}
                        onChange={handleUpcomingAppointmentsDateChange}
                        className="date-input"
                      />
                      <button 
                        className="nav-arrow"
                        onClick={() => navigateUpcomingAppointmentsDate('next')}
                        title="Next day"
                      >
                        →
                      </button>
                    </div>
                  </div>
                  
                  {upcomingAppointmentsLoading ? (
                    <div className="loading-state">
                      <p>{t('loadingAppointments')}</p>
                    </div>
                  ) : upcomingTimeslots.length === 0 ? (
                    <div className="empty-state">
                      <p>{t('noUpcomingAppointments')} {formatDate(upcomingAppointmentsDate)}</p>
                    </div>
                  ) : (
                    <div className="upcoming-timeslots">
                      {upcomingTimeslots.map((data) => (
                        <div key={`${data.timeslot.date}-${data.timeslot.startTime}-${data.timeslot.endTime}`} className="timeslot-card">
                          <div className="timeslot-header">
                            <h3>{formatTime(data.timeslot.startTime)} - {formatTime(data.timeslot.endTime)}</h3>
                            <span className="capacity-info">
                              {data.clientsData?.bookedCount || 0} / {data.timeslot.capacity}
                            </span>
                          </div>
                          <div className="timeslot-appointments">
                            {data.clientsData?.bookedClients?.length ? (
                              <div className="appointments-grid">
                                {data.clientsData.bookedClients.map((appointment) => {
                                  const matchedClient = allClients.find(c => c.id === appointment.userId);
                                  return (
                                  <div key={appointment.id} className={`appointment-item ${removingAppointments.has(appointment.id) ? 'removing' : ''}`}>
                                    <span className="client-name">
                                      {appointment.userName}
                                      {matchedClient && (
                                        <span className={`cardiology-heart ${matchedClient.cardiologyProof ? 'has-proof' : 'no-proof'}`} title={t('cardiologyProof')}>
                                          ♥
                                        </span>
                                      )}
                                    </span>
                                    {appointment.status === 1 && (
                                      <button 
                                        className="cancel-appointment-btn"
                                        onClick={() => handleCancelAppointment(appointment.id)}
                                        title={`Cancel appointment for ${appointment.userName}`}
                                      >
                                        ×
                                      </button>
                                    )}
                                  </div>
                                  );
                                })}
                              </div>
                            ) : data.clientsData === null ? (
                              <div className="appointment-item">
                                <span className="client-name" style={{ fontStyle: 'italic', color: '#718096' }}>
                                  Error loading clients
                                </span>
                              </div>
                            ) : (
                              <div className="appointment-item">
                                <span className="client-name" style={{ fontStyle: 'italic', color: '#718096' }}>
                                  No appointments found
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              </div>
            )}

            {activeAdminTab === 'subscriptions' && (
              <div className="subscriptions-view">
                <section className="active-subscriptions">
                  <div className="section-header">
                    <h2>{t('activeSubscriptions')}</h2>
                    <div className="search-form">
                      <input
                        type="text"
                        placeholder={t('searchClientsByName')}
                        value={subscriptionSearchTerm}
                        onChange={handleSubscriptionSearch}
                        className="search-input"
                      />
                    </div>
                  </div>
                  {subscriptionDataLoading ? (
                    <div className="loading-state">
                      <p>{t('loadingSubscriptions')}</p>
                    </div>
                  ) : unfilteredSubscriptionData.length === 0 ? (
                    <div className="empty-state">
                      <p>{subscriptionSearchTerm.trim() ? t('noClientsFound') + ` "${subscriptionSearchTerm}"` : t('noActiveSubscriptions')}</p>
                    </div>
                  ) : (
                    <div className="subscriptions-table">
                      <table>
                        <thead>
                          <tr>
                            <th>{t('name')}</th>
                            <th>{t('subscriptionPeriod')}</th>
                            <th>{t('monthlyLimit')}</th>
                            <th>{t('usedThisMonth')}</th>
                            <th>{t('attendedAppointmentCount')}</th>
                            <th>{t('appointmentDates')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredSubscriptionData.map((data) => {
                            const { client, monthlyAppointments, appointmentDates } = data;
                            const subscription = client.subscription!;
                            const status = getSubscriptionStatus(subscription.actualEndDate || subscription.endDate);
                            const monthlyLimit = client.monthlyLimit || subscription.monthlyLimit;

                            return (
                              <tr key={client.id}>
                                <td data-label={t('name')}>
                                  {client.name}
                                  <span className={`cardiology-heart ${client.cardiologyProof ? 'has-proof' : 'no-proof'}`} title={t('cardiologyProof')}>
                                    ♥
                                  </span>
                                </td>
                                <td data-label={t('subscriptionPeriod')}>
                                  <span className={`subscription-period ${status}`}>
                                    {formatDateRange(
                                      subscription.actualStartDate || subscription.startDate,
                                      subscription.actualEndDate || subscription.endDate
                                    )}
                                  </span>
                                </td>
                                <td className="monthly-limit" data-label={t('monthlyLimit')}>{monthlyLimit}</td>
                                <td className="appointments-used" data-label={t('usedThisMonth')}>
                                  <span className={(subscription.validAppointmentCount ?? monthlyAppointments.length) >= monthlyLimit ? 'limit-reached' : ''}>
                                    {subscription.validAppointmentCount ?? monthlyAppointments.length}
                                  </span>
                                </td>
                                <td className="attended-count" data-label={t('attendedAppointmentCount')}>
                                  {subscription.attendedAppointmentCount ?? 0}
                                </td>
                                <td className="appointment-dates" data-label={t('appointmentDates')}>
                                  {formatAppointmentDates(appointmentDates)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </section>
              </div>
            )}

            {activeAdminTab === 'clients' && (
              <div className="clients-management">
                {/* Stats Section moved from Overview */}
                <section className="stats-section">
                  <div className="stats-grid">
                    <div
                      className={`stat-card ${clientFilter === 'active' ? 'active-filter' : ''}`}
                      onClick={() => handleClientFilterChange('active')}
                      role="button"
                      tabIndex={0}
                      onKeyPress={(e) => e.key === 'Enter' && handleClientFilterChange('active')}
                    >
                      <h3>{toGreekUpperCase(t('activeSubscriptions'))}</h3>
                      <p className="stat-number">
                        {activeClientsCount}
                      </p>
                    </div>
                    <div
                      className={`stat-card ${clientFilter === 'inactive' ? 'active-filter' : ''}`}
                      onClick={() => handleClientFilterChange('inactive')}
                      role="button"
                      tabIndex={0}
                      onKeyPress={(e) => e.key === 'Enter' && handleClientFilterChange('inactive')}
                    >
                      <h3>{toGreekUpperCase(t('inactiveClients'))}</h3>
                      <p className="stat-number">
                        {inactiveClientsCount}
                      </p>
                    </div>
                    <div
                      className="stat-card"
                      onClick={() => setClientFilter(null)}
                      role="button"
                      tabIndex={0}
                      onKeyPress={(e) => e.key === 'Enter' && setClientFilter(null)}
                    >
                      <h3>{toGreekUpperCase(t('totalClients'))}</h3>
                      <p className="stat-number">{allClients.length}</p>
                    </div>
                  </div>
                </section>

                <div className="section-header">
                  <h2>{t('clientManagement')}</h2>
                  <div className="header-actions">
                    <div className="search-form">
                      <input
                        type="text"
                        placeholder={t('searchClients')}
                        value={searchTerm}
                        onChange={handleSearch}
                        className="search-input"
                      />
                    </div>
                    <button onClick={handleAddClient} className="primary-button">
                      {t('addNewClient')}
                    </button>
                  </div>
                </div>
                
                {filteredClients.length === 0 ? (
                  <div className="empty-state">
                    <p>{searchTerm ? t('noClientsFound') + ` "${searchTerm}"` : t('noClientsFoundGeneral')}</p>
                  </div>
                ) : (
                  <div className="clients-table">
                    <table>
                      <thead>
                        <tr>
                          <th>{t('name')}</th>
                          <th>{t('email')}</th>
                          <th>{t('subscriptionExpires')}</th>
                          <th>{t('actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredClients.map((client) => (
                          <tr key={client.id}>
                            <td data-label={t('name')}>
                              {client.name}
                              <span className={`cardiology-heart ${client.cardiologyProof ? 'has-proof' : 'no-proof'}`} title={t('cardiologyProof')}>
                                ♥
                              </span>
                            </td>
                            <td data-label={t('email')}>{client.email}</td>
                            <td data-label={t('subscriptionExpires')}>
                              {!client.isActive ? (
                                <span className="subscription-status disabled">{t('disabled')}</span>
                              ) : client.subscription ? (
                                <span className={`subscription-period ${getSubscriptionStatus(client.subscription.actualEndDate || client.subscription.endDate, client.subscription.isFrozen, client.isActive)}`}>
                                  {client.subscription.isFrozen ? t('frozen') : formatDate(client.subscription.actualEndDate || client.subscription.endDate)}
                                </span>
                              ) : (
                                <span className="subscription-status none">{t('noSubscription')}</span>
                              )}
                            </td>
                            <td data-label={t('actions')}>
                              <div className="action-buttons">
                                <button 
                                  className="edit-button info"
                                  onClick={() => handleEditInfo(client)}
                                  title={t('info')}
                                >
                                  {t('info')}
                                </button>
                                <button 
                                  className="edit-button subscription"
                                  onClick={() => handleEditSubscription(client)}
                                  title={t('subscription')}
                                >
                                  {t('subscription')}
                                </button>
                                <button 
                                  className={`edit-button appointments ${!client.subscription ? 'disabled' : ''}`}
                                  onClick={() => client.subscription && handleEditAppointments(client)}
                                  title={!client.subscription ? t('noSubscriptionForAppointments') : t('appointments')}
                                  disabled={!client.subscription}
                                >
                                  {t('appointments')}
                                </button>
                                <button 
                                  className="edit-button body-fat"
                                  onClick={() => handleBodyFatMeasurements(client)}
                                  title={t('bodyFatMeasurements')}
                                >
                                  {t('bodyFatMeasurements')}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {activeAdminTab === 'timeslots' && (
              <TimeslotManagement />
            )}

            {activeAdminTab === 'settings' && (
              <Settings 
                settings={settings}
                isLoading={settingsLoading}
                error={settingsError}
                onSettingsUpdate={refreshSettings}
              />
            )}
          </div>
        )}

        {isClient() && (
          <div className="client-dashboard">
            {/* Client Navigation */}
            <nav className="dashboard-nav">
              <button 
                className={`nav-tab ${activeClientTab === 'overview' ? 'active' : ''}`}
                onClick={() => setActiveClientTab('overview')}
              >
                {t('overview')}
              </button>
              <button 
                className={`nav-tab ${activeClientTab === 'book' ? 'active' : ''} ${isSubscriptionExpired(clientProfile) ? 'disabled' : ''}`}
                onClick={() => !isSubscriptionExpired(clientProfile) && setActiveClientTab('book')}
                disabled={isSubscriptionExpired(clientProfile)}
              >
                {t('bookAppointment')}
              </button>
              <button 
                className={`nav-tab ${activeClientTab === 'profile' ? 'active' : ''}`}
                onClick={() => setActiveClientTab('profile')}
              >
                {t('myProfile')}
              </button>
            </nav>

            {/* Client Content */}
            {activeClientTab === 'overview' && (
              <div className="client-overview">
                <section className="quick-stats">
                  <div className="stats-grid">
                    <div className="stat-card">
                      <h3>{toGreekUpperCase(t('totalAppointments'))}</h3>
                      <p className="stat-number">
                        {clientProfile?.subscription?.completedAppointmentCount !== undefined && clientProfile?.subscription?.completedAppointmentCount !== null
                          ? clientProfile.subscription.completedAppointmentCount
                          : '-'}
                      </p>
                    </div>
                    <div className="stat-card">
                      <h3>{toGreekUpperCase(t('upcoming'))}</h3>
                      <p className="stat-number">
                        {clientProfile?.subscription?.upcomingAppointmentCount !== undefined && clientProfile?.subscription?.upcomingAppointmentCount !== null
                          ? clientProfile.subscription.upcomingAppointmentCount
                          : '-'}
                      </p>
                    </div>
                    <div className="stat-card">
                      <h3>{toGreekUpperCase(t('monthlyLimit'))}</h3>
                      <p className="stat-number">
                        {clientProfile?.subscription?.monthlyLimit !== undefined && clientProfile?.subscription?.monthlyLimit !== null
                          ? clientProfile.subscription.monthlyLimit
                          : '-'}
                      </p>
                    </div>
                  </div>
                </section>

                <section className="upcoming-appointments">
                  <h2>{t('upcomingAppointments')}</h2>
                  {getUpcomingAppointments().length === 0 ? (
                    <div className="empty-state">
                      <p>{t('noUpcomingAppointmentsShort')}</p>
                      {!isSubscriptionExpired(clientProfile) && (
                        <button 
                          className="primary-button"
                          onClick={() => setActiveClientTab('book')}
                        >
                          {t('bookNewAppointment')}
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="appointments-grid">
                      {getUpcomingAppointments().map((appointment) => (
                        <div key={appointment.id} className="appointment-card">
                          <div className="appointment-header">
                            <h3>{formatDate(appointment.appointmentDate, { weekday: true, short: true })}</h3>
                            {getStatusBadge(appointment.status)}
                          </div>
                          <div className="appointment-details">
                            <p>
                              <strong>{t('time')}:</strong> {formatTime(appointment.startTime)} - {formatTime(appointment.endTime)}
                            </p>
                            <p>
                              <strong>{t('booked')}:</strong> {formatDate(appointment.bookedAt)}
                            </p>
                          </div>
                          {appointment.status === 1 && (
                            <div className="appointment-actions">
                              <button 
                                className="cancel-button"
                                onClick={() => handleCancelAppointment(appointment.id)}
                              >
                                {t('cancel')}
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </section>

                {/* Appointment History */}
                <section className="appointments-section">
                  <h2>{t('appointmentHistory')}</h2>
                  {getPastAppointments().length === 0 ? (
                                          <div className="no-appointments">
                        <p>{t('noAppointmentHistory')}</p>
                      </div>
                  ) : (
                    <div className="appointments-list">
                      {getPastAppointments().slice(0, 5).map((appointment) => (
                        <div key={appointment.id} className="appointment-history-item">
                          <div className="appointment-info">
                            <div className="appointment-date">
                              {formatDate(appointment.appointmentDate)}
                            </div>
                            <div className="appointment-time">
                              {formatTime(appointment.startTime)} - {formatTime(appointment.endTime)}
                            </div>
                          </div>
                          <div className="appointment-status">
                            {getStatusBadge(appointment.status)}
                          </div>
                        </div>
                      ))}
                      {getPastAppointments().length > 5 && (
                        <div className="show-more">
                          {t('showingLastAppointments')}
                        </div>
                      )}
                    </div>
                  )}
                </section>
              </div>
            )}

            {activeClientTab === 'book' && (
              <BookAppointment 
                appointments={appointments} 
                profile={clientProfile} 
                onSuccess={setSuccess}
                onError={setErrorWithAutoDismiss}
              />
            )}

            {activeClientTab === 'profile' && (
              <ClientProfile />
            )}
          </div>
        )}
      </main>

      {/* Modals */}
      <AddClientModal 
        isOpen={showAddClientModal}
        onClose={() => setShowAddClientModal(false)}
        onClientAdded={handleClientAdded}
      />

      <EditClientInfoModal 
        isOpen={showEditInfoModal}
        onClose={() => setShowEditInfoModal(false)}
        onClientUpdated={handleClientUpdated}
        client={selectedClient}
      />

      <EditClientSubscriptionModal 
        isOpen={showEditSubscriptionModal}
        onClose={() => setShowEditSubscriptionModal(false)}
        onClientUpdated={handleClientUpdated}
        client={selectedClient}
      />

      <EditClientAppointmentsModal 
        isOpen={showEditAppointmentsModal}
        onClose={() => setShowEditAppointmentsModal(false)}
        onClientUpdated={handleClientUpdated}
        client={selectedClient}
      />

      <BodyFatMeasurementsModal
        isOpen={showBodyFatModal}
        onClose={() => setShowBodyFatModal(false)}
        client={selectedClient}
      />

      <ConfirmationDialog
        isOpen={confirmation.isOpen}
        title={confirmation.title}
        message={confirmation.message}
        confirmText={confirmation.confirmText}
        cancelText={confirmation.cancelText}
        type={confirmation.type}
        onConfirm={confirmation.onConfirm}
        onCancel={confirmation.onCancel}
      />

      <AdminCancelAppointmentDialog
        isOpen={cancelDialogAppointmentId !== null}
        isLoading={isCancellingAsAdmin}
        onConfirm={handleConfirmCancelAsAdmin}
        onCancel={() => setCancelDialogAppointmentId(null)}
      />
    </div>
  );
};

export default Dashboard;